import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import httpInstances from '@/api/axiosInstance';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { handleThunkError } from '@/utils/thunkErrorHandler';

export type ApiKeyOutput = {
  prefixedRawApiKey?: string;
  id: number;
  lastThreeCharacters: string;
  label: string;
  creationDate: string;
  lastUsed?: string;
};

type ApiKeyListState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'creating';
  error: string | null;
  generated: boolean;
  apiKeyOutputs: ApiKeyOutput[];
};

const initialState: ApiKeyListState = {
  status: 'idle',
  error: null,
  generated: false,
  apiKeyOutputs: []
};

export const createApiKey = createAsyncThunk(
  'createApiKey',
  async (label: string, { rejectWithValue }) => {
    try {
      const body = { label };
      const { data } = await httpInstances.axiosInternalInstance.post('proxy/auth/apikey', body, {
        headers: { Accept: 'application/json' }
      });
      return data;
    } catch (error) {
      return handleThunkError(error, rejectWithValue) as never;
    }
  }
);

export const deleteApiKey = createAsyncThunk(
  'deleteApiKey',
  async (id: number, { rejectWithValue }) => {
    try {
      const { status } = await httpInstances.axiosInternalInstance.delete(
        `proxy/auth/apikey/${id}`,
        { headers: { Accept: 'application/json' } }
      );
      if (status === 204) {
        return id;
      }
      rejectWithValue(`Couldn't deleted apiKey with Id${id}`);
    } catch (error) {
      return handleThunkError(error, rejectWithValue) as never;
    }
  }
);

export const getApiKeys = createAsyncThunk('getApiKeys', async (_, { rejectWithValue }) => {
  try {
    const { data } = await httpInstances.axiosInternalInstance.get(`proxy/auth/apikey`);
    return data.apiKeyOutputs;
  } catch (error) {
    return handleThunkError(error, rejectWithValue) as never;
  }
});

const apiKeyListSlice = createSlice({
  name: 'apiKeyList',
  initialState,
  reducers: {
    resetGenerated(state) {
      state.generated = false;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getApiKeys.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getApiKeys.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.apiKeyOutputs = action.payload;
      })
      .addCase(getApiKeys.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error.message);
      })
      .addCase(createApiKey.pending, state => {
        state.status = 'creating';
        state.generated = false;
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.apiKeyOutputs.push(action.payload);
        state.generated = true;
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error.message);
      })
      .addCase(deleteApiKey.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteApiKey.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.apiKeyOutputs = state.apiKeyOutputs.filter(apiKey => apiKey.id !== action.payload);
      })
      .addCase(deleteApiKey.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error.message);
      });
  }
});
export const { resetGenerated } = apiKeyListSlice.actions;
export default apiKeyListSlice.reducer;
